import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { FONTS, Serif } from '@grayskl/ui';
import portraitUrl from './assets/grayskl-portrait.png';

// The study of a bog witch, translated to CSS. Every color here is sampled
// from the portrait: chalkboard slate, moon-purple neon, candle gold,
// mossy frog green, parchment, sticky-note yellow.
const theme = {
  void: '#120a18',
  board: '#1a1024',
  boardDeep: '#160d1e',
  frame: '#43305a',
  line: '#382650',
  slate: '#9388a8',
  parchment: '#ecdfc3',
  parchmentDim: '#c6b89c',
  neonPurple: '#c084fc',
  moonPurple: '#a56cff',
  candleGold: '#e6b843',
  ember: '#e8873c',
  moss: '#8fbc63',
  paper: '#f2ead4',
  paperInk: '#3a3026',
  sticky: '#f2d977',
} as const;

type AppStatus = 'in-development' | 'available';

interface AppLink {
  label: string;
  href: string;
}

interface AppAssurance {
  icon: string;
  text: string;
  href?: string;
  hrefLabel?: string;
}

interface AppEntry {
  slug: string;
  sigil: string;
  label: string;
  title: string;
  description: string;
  status: AppStatus;
  links: readonly AppLink[];
  assurances?: readonly AppAssurance[];
  // GitHub "latest release" API URL — renders a live version badge.
  versionApi?: string;
  hint?: string;
  finePrint?: string;
}

const apps: readonly AppEntry[] = [
  {
    slug: 'steady',
    sigil: '💜',
    label: 'For flare days',
    title: 'Steady',
    description:
      'Symptom, vital, and flare tracking for people living with EDS, POTS, and dysautonomia. Designed by someone who needs it.',
    status: 'available',
    links: [
      {
        label: '⬇ Android · .apk',
        href: 'https://github.com/GraySKL/Steady/releases/latest/download/steady.apk',
      },
      {
        label: 'iPhone & web ↗',
        href: 'https://grayskl.github.io/Steady/',
      },
    ],
    versionApi: 'https://api.github.com/repos/GraySKL/Steady/releases/latest',
    assurances: [
      {
        icon: '🔒',
        text: 'Everything stays on your device — no account, no cloud, no tracking. The Android app can’t even go online: the operating system itself blocks it.',
      },
      {
        icon: '📖',
        text: 'Open source (MIT) — every line of code is public for anyone to inspect.',
        href: 'https://github.com/GraySKL/Steady',
        hrefLabel: 'read the code',
      },
      {
        icon: '🔄',
        text: 'Updates ship on GitHub. The Android button above always grabs the newest release, and the web app updates itself.',
      },
    ],
    hint: 'Android will ask permission to install apps from outside the Play Store — that prompt is expected.',
    finePrint:
      'Steady is a self-tracking companion, not a medical device. It doesn’t diagnose, treat, or replace your care team.',
  },
  {
    slug: 'command-center',
    sigil: '🗳️',
    label: 'For organizers',
    title: 'Command Center',
    description:
      'A CRM and email platform for progressive nonprofits — contacts, lists, events, campaigns. Built so a volunteer coordinator can send beautiful emails without a marketing team.',
    status: 'in-development',
    links: [],
  },
  {
    slug: 'puente-la-divide',
    sigil: '🌉',
    label: 'For new arrivals',
    title: 'Puente La Divide',
    description:
      'Survival English for Spanish-first immigrants. Tía Marisol walks you through real moments — la parada, la casa, el trabajo — where you hear the English, see the Spanish, and meet your rights exactly when they matter. Free, bilingual, fully offline.',
    status: 'available',
    links: [
      {
        label: '⬇ Android · .apk',
        href: 'https://github.com/GraySKL/Puente-la-divide/releases/download/test-latest/puente-la-divide.apk',
      },
      {
        label: 'iPhone & web ↗',
        href: 'https://grayskl.github.io/Puente-la-divide/',
      },
    ],
    assurances: [
      {
        icon: '🔒',
        text: 'Built for privacy under pressure — no accounts, no analytics, no location, no microphone. Everything stays on your device, and the app works fully offline.',
      },
      {
        icon: '📖',
        text: 'Open source — every line of code is public for anyone to inspect.',
        href: 'https://github.com/GraySKL/Puente-la-divide',
        hrefLabel: 'read the code',
      },
      {
        icon: '🗽',
        text: 'Rights content is never invented — it comes straight from ILRC Red Cards, the ACLU, NILC, and United We Dream, down to annotated sample warrants and an En Carolina del Norte section.',
      },
      {
        icon: '📵',
        text: 'Distributed here and on GitHub only — no app stores, on purpose. The buttons above always grab the newest build.',
      },
    ],
    hint:
      'Early test build. Android will ask permission to install apps from outside the Play Store — that prompt is expected. Uninstall an older test build before installing a new one.',
    finePrint:
      'Puente La Divide offers plain-language guidance, not legal advice — an accredited immigration attorney or DOJ-accredited representative is the right person for your case.',
  },
];

export function App() {
  return (
    <div style={{ minHeight: '100vh', color: theme.parchment }}>
      <div style={{ maxWidth: 1020, margin: '0 auto', padding: '36px 24px 72px' }}>
        <Header />
        <Hero />
        <ChalkBanner />
        <AppsSection />
        <StudyDesk />
        <Footer />
      </div>
    </div>
  );
}

function Header() {
  return (
    <header style={{ marginBottom: 64 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          paddingBottom: 22,
        }}
      >
        <Serif style={{ fontSize: 24, letterSpacing: '0.02em' }}>
          GraySKL <span style={{ color: theme.moonPurple, fontSize: 15 }}>☾</span>
        </Serif>
        <nav
          style={{
            display: 'flex',
            gap: 24,
            fontFamily: FONTS.sans,
            fontSize: 13,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: theme.slate,
          }}
        >
          <a href="#apps">Apps</a>
          <a href="#study">The study</a>
        </nav>
      </div>
      <div className="rainbow-line" />
    </header>
  );
}

function Hero() {
  return (
    <section
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 52,
        marginBottom: 88,
      }}
    >
      <div style={{ flex: '1 1 340px', minWidth: 280 }}>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 11,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: theme.candleGold,
            marginBottom: 22,
          }}
          className="candle-glow"
        >
          ✦ bog witch by day · code witch by night ✦
        </div>
        <h1
          className="neon"
          style={{
            margin: 0,
            fontFamily: FONTS.serif,
            fontStyle: 'italic',
            fontWeight: 400,
            fontSize: 66,
            lineHeight: 1.02,
            letterSpacing: '-0.015em',
          }}
        >
          syntax is magic.
        </h1>
        <p
          style={{
            marginTop: 30,
            marginBottom: 0,
            fontSize: 18,
            color: theme.parchmentDim,
            maxWidth: 440,
            lineHeight: 1.6,
          }}
        >
          Small, honest apps from{' '}
          <strong style={{ color: theme.candleGold, fontWeight: 500 }}>GraySKL</strong> — for the
          people who actually have to use them. Good vibes &amp; good code.
        </p>
        <StickyNote />
      </div>
      <div style={{ flex: '1 1 300px', minWidth: 280, maxWidth: 440 }}>
        <img
          src={portraitUrl}
          alt="GraySKL — a silver-haired bog witch coding at a candlelit desk with a black cat and friendly frogs"
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            borderRadius: 8,
            border: `1px solid ${theme.frame}`,
            boxShadow: '0 0 46px rgba(139, 82, 246, 0.28), 0 24px 60px rgba(0, 0, 0, 0.55)',
          }}
        />
      </div>
    </section>
  );
}

function StickyNote() {
  return (
    <div
      className="hand"
      style={{
        display: 'inline-block',
        marginTop: 34,
        padding: '10px 18px 12px',
        background: theme.sticky,
        color: '#4a3a10',
        fontSize: 24,
        lineHeight: 1.1,
        transform: 'rotate(-2.5deg)',
        borderRadius: 2,
        boxShadow: '0 8px 18px rgba(0, 0, 0, 0.45)',
      }}
    >
      you got this! <span style={{ color: '#7c4bb8' }}>♥</span>
    </div>
  );
}

function ChalkBanner() {
  return (
    <div
      style={{
        background: theme.boardDeep,
        border: `3px solid ${theme.frame}`,
        outline: `1px solid ${theme.line}`,
        outlineOffset: -8,
        borderRadius: 6,
        padding: '26px 28px',
        marginBottom: 88,
        textAlign: 'center',
        boxShadow: 'inset 0 0 60px rgba(0, 0, 0, 0.45)',
      }}
    >
      <span
        className="hand"
        style={{
          fontSize: 34,
          letterSpacing: '0.06em',
          color: '#e3dcc9',
        }}
      >
        <span style={{ color: theme.candleGold }}>✦</span> bugs fear me{' '}
        <span style={{ color: theme.moss }}>·</span> code obeys me{' '}
        <span style={{ color: theme.candleGold }}>✦</span>{' '}
        <span style={{ color: theme.moonPurple }}>☾</span>
      </span>
    </div>
  );
}

function AppsSection() {
  return (
    <section id="apps" style={{ marginBottom: 88 }}>
      <SectionLabel>Potions on the shelf — the apps</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        {apps.map((app) => (
          <AppCard key={app.slug} app={app} />
        ))}
      </div>
    </section>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontFamily: FONTS.sans,
        fontSize: 12,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: theme.slate,
        marginBottom: 28,
      }}
    >
      {children}
    </div>
  );
}

function AppCard({ app }: { app: AppEntry }) {
  return (
    <article
      style={{
        background: theme.board,
        border: `1px solid ${theme.frame}`,
        borderLeft: `3px solid ${theme.moonPurple}`,
        borderRadius: 4,
        padding: '26px 28px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        boxShadow: '0 14px 34px rgba(0, 0, 0, 0.35)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <span
          style={{
            fontFamily: FONTS.sans,
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: theme.neonPurple,
          }}
        >
          {app.label}
        </span>
        <span style={{ fontSize: 20 }} aria-hidden="true">
          {app.sigil}
        </span>
      </div>
      <Serif as="h2" style={{ margin: 0, fontSize: 30, color: theme.parchment }}>
        {app.title}
      </Serif>
      <p style={{ margin: 0, color: theme.parchmentDim, fontSize: 16, lineHeight: 1.55 }}>
        {app.description}
      </p>
      <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        {app.links.length > 0 ? (
          <>
            {app.links.map((link) => (
              <DownloadBtn key={link.href} link={link} />
            ))}
            {app.versionApi && <VersionBadge api={app.versionApi} />}
          </>
        ) : (
          <StatusPill />
        )}
      </div>
      {app.assurances && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {app.assurances.map((a) => (
            <div
              key={a.icon}
              style={{
                display: 'flex',
                gap: 10,
                fontFamily: FONTS.sans,
                fontSize: 13,
                lineHeight: 1.6,
                color: theme.parchmentDim,
              }}
            >
              <span aria-hidden="true" style={{ flexShrink: 0 }}>
                {a.icon}
              </span>
              <span>
                {a.text}
                {a.href && (
                  <>
                    {' '}
                    <a
                      href={a.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: theme.moss, textDecoration: 'underline', textUnderlineOffset: 3 }}
                    >
                      {a.hrefLabel ?? a.href}
                    </a>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      {app.hint && (
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 11.5,
            lineHeight: 1.6,
            color: theme.slate,
          }}
        >
          {app.hint}
        </div>
      )}
      {app.finePrint && (
        <div
          style={{
            fontFamily: FONTS.sans,
            fontSize: 12.5,
            lineHeight: 1.6,
            color: theme.slate,
            fontStyle: 'italic',
          }}
        >
          {app.finePrint}
        </div>
      )}
    </article>
  );
}

// Live version chip — asks GitHub for the latest release so the site always
// shows the current update without a redeploy. Renders nothing if the
// request fails (rate limit, offline).
function VersionBadge({ api }: { api: string }) {
  const [version, setVersion] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(api)
      .then((r) => (r.ok ? r.json() : null))
      .then((rel: { tag_name?: string; published_at?: string } | null) => {
        if (cancelled || !rel?.tag_name) return;
        setVersion(rel.tag_name);
        if (rel.published_at) {
          setDate(
            new Date(rel.published_at).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric',
            }),
          );
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api]);

  if (!version) return null;
  return (
    <span
      style={{
        fontFamily: FONTS.mono,
        fontSize: 11.5,
        letterSpacing: '0.06em',
        color: theme.moss,
      }}
    >
      {version}
      {date ? ` · updated ${date}` : ''}
    </span>
  );
}

function DownloadBtn({ link }: { link: AppLink }) {
  return (
    <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-block',
        padding: '8px 16px',
        border: `1px solid ${theme.candleGold}`,
        borderRadius: 3,
        fontFamily: FONTS.mono,
        fontSize: 12.5,
        letterSpacing: '0.06em',
        color: theme.candleGold,
        textDecoration: 'none',
        boxShadow: '0 0 14px rgba(230, 184, 67, 0.15)',
      }}
      className="candle-glow"
    >
      {link.label}
    </a>
  );
}

function StatusPill() {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 12px',
        border: `1px solid ${theme.candleGold}`,
        borderRadius: 2,
        fontFamily: FONTS.mono,
        fontSize: 11,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: theme.candleGold,
      }}
    >
      🕯 still brewing
    </span>
  );
}

// ── The study: the code screen + the spiral notebook from the desk ──────────

const codeStyles: Record<'comment' | 'kw' | 'fn' | 'val' | 'plain', CSSProperties> = {
  comment: { color: theme.moss },
  kw: { color: theme.neonPurple },
  fn: { color: theme.candleGold },
  val: { color: theme.ember },
  plain: { color: '#d9cfe8' },
};

function StudyDesk() {
  return (
    <section id="study" style={{ marginBottom: 88 }}>
      <SectionLabel>On the desk</SectionLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'stretch' }}>
        <CodeScreen />
        <TodaysPlan />
      </div>
    </section>
  );
}

function CodeScreen() {
  const s = codeStyles;
  return (
    <div
      style={{
        flex: '1.4 1 380px',
        minWidth: 300,
        background: '#140c1c',
        border: `1px solid ${theme.frame}`,
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 0 34px rgba(139, 82, 246, 0.16), 0 18px 40px rgba(0, 0, 0, 0.45)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '10px 14px',
          borderBottom: `1px solid ${theme.line}`,
          background: theme.boardDeep,
        }}
      >
        {[theme.ember, theme.candleGold, theme.moss].map((c) => (
          <span
            key={c}
            style={{ width: 10, height: 10, borderRadius: '50%', background: c, opacity: 0.8 }}
          />
        ))}
        <span
          style={{
            marginLeft: 8,
            fontFamily: FONTS.mono,
            fontSize: 11,
            color: theme.slate,
            letterSpacing: '0.06em',
          }}
        >
          the_daily_ritual.py
        </span>
      </div>
      <pre
        style={{
          margin: 0,
          padding: '20px 22px 24px',
          fontFamily: FONTS.mono,
          fontSize: 13.5,
          lineHeight: 1.75,
          overflowX: 'auto',
        }}
      >
        <span style={s.comment}># another beautiful day in the bog ✨</span>
        {'\n\n'}
        <span style={s.kw}>def</span> <span style={s.fn}>brew_success</span>
        <span style={s.plain}>():</span>
        {'\n    '}
        <span style={s.plain}>coffee = </span>
        <span style={s.kw}>True</span>
        {'\n    '}
        <span style={s.plain}>focus = </span>
        <span style={s.kw}>True</span>
        {'\n    '}
        <span style={s.plain}>magic = </span>
        <span style={s.kw}>True</span>
        {'\n    '}
        <span style={s.plain}>bugs = </span>
        <span style={s.val}>0</span>
        {'\n    '}
        <span style={s.plain}>happiness = </span>
        <span style={s.val}>MAX</span>
        {'\n    '}
        <span style={s.kw}>return</span>
        <span style={s.plain}> (coffee, focus, magic, bugs, happiness)</span>
        {'\n\n'}
        <span style={s.kw}>while</span> <span style={s.kw}>True</span>
        <span style={s.plain}>:</span>
        {'\n    '}
        <span style={s.fn}>code</span>
        <span style={s.plain}>()</span>
        {'\n    '}
        <span style={s.fn}>drink_coffee</span>
        <span style={s.plain}>()</span>
        {'\n    '}
        <span style={s.fn}>hex</span>
        <span style={s.plain}>(bugs)</span>
        {'\n    '}
        <span style={s.fn}>consult_the_llama</span>
        <span style={s.plain}>()  </span>
        <span style={s.comment}># she knows things 🦙</span>
        {'\n    '}
        <span style={s.fn}>celebrate_small_wins</span>
        <span style={s.plain}>()</span>
        {'\n    '}
        <span style={s.fn}>repeat</span>
        <span style={s.plain}>()</span>
      </pre>
    </div>
  );
}

const planItems = ['code', 'hex', 'manifest', 'help frogs', 'feed the llama', 'take naps'] as const;

// A marker-pen rainbow doodled in the notebook margin.
function DoodleRainbow() {
  const arcs = [
    { r: 24, c: '#c0684a' },
    { r: 18, c: '#c9a24a' },
    { r: 12, c: '#7f9e5a' },
    { r: 6, c: '#8a6bb8' },
  ];
  return (
    <svg viewBox="0 0 60 34" width={58} height={33} aria-hidden="true">
      {arcs.map((a) => (
        <path
          key={a.c}
          d={`M${30 - a.r} 32 A${a.r} ${a.r} 0 0 1 ${30 + a.r} 32`}
          fill="none"
          stroke={a.c}
          strokeWidth={4.5}
          strokeLinecap="round"
          opacity={0.8}
        />
      ))}
    </svg>
  );
}

function TodaysPlan() {
  return (
    <div
      style={{
        flex: '1 1 260px',
        minWidth: 250,
        background: theme.paper,
        color: theme.paperInk,
        borderRadius: 4,
        padding: '26px 30px 30px 42px',
        transform: 'rotate(1.2deg)',
        boxShadow: '0 18px 40px rgba(0, 0, 0, 0.5)',
        backgroundImage: `repeating-linear-gradient(
          transparent,
          transparent 35px,
          rgba(58, 48, 38, 0.12) 35px,
          rgba(58, 48, 38, 0.12) 36px
        )`,
      }}
    >
      <div
        className="hand"
        style={{
          fontSize: 30,
          fontWeight: 700,
          letterSpacing: '0.04em',
          borderBottom: `2px solid rgba(58, 48, 38, 0.35)`,
          display: 'inline-block',
          marginBottom: 14,
        }}
      >
        TODAY&rsquo;S PLAN:
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {planItems.map((item) => (
          <li
            key={item}
            className="hand"
            style={{ fontSize: 26, lineHeight: '36px', display: 'flex', gap: 12 }}
          >
            <span style={{ color: '#5a7a3a' }}>☑</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <div
        style={{
          marginTop: 12,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
        }}
      >
        <span className="hand" style={{ fontSize: 24, color: '#7c4bb8' }}>
          ♥
        </span>
        <DoodleRainbow />
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer>
      <p
        style={{
          margin: '0 0 28px',
          fontFamily: FONTS.sans,
          fontSize: 12,
          lineHeight: 1.7,
          color: theme.slate,
          maxWidth: 640,
        }}
      >
        The fine print: everything here is offered as-is, with care but without warranty. Nothing
        on this site is medical, legal, or professional advice — take what helps, and lean on the
        humans who know your situation.
      </p>
      <div className="rainbow-line" />
      <div
        style={{
          paddingTop: 34,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 12,
          fontFamily: FONTS.sans,
          fontSize: 13,
          color: theme.slate,
        }}
      >
        <span>
          © GraySKL — chaos coordinator <span aria-hidden="true">🐸 🦙</span>
        </span>
        <a href="https://github.com/GraySKL" style={{ color: theme.parchmentDim }}>
          github.com/GraySKL
        </a>
      </div>
    </footer>
  );
}
